import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * SectionReveal
 * A dynamic scroll-triggered wrapper that provides:
 *  - directional slide (up/down/left/right)
 *  - blur-in
 *  - scale-in
 *  - staggered child reveal
 *  - optional parallax follow while section is in view
 */
export const SectionReveal = ({
  children,
  as = 'div',
  variant = 'up', // up | down | left | right | fade | scale | mask
  delay = 0,
  duration = 0.9,
  amount = 0.2,
  once = true,
  className = '',
  ...rest
}) => {
  const initialMap = {
    up: { opacity: 0, y: 60, filter: 'blur(6px)' },
    down: { opacity: 0, y: -60, filter: 'blur(6px)' },
    left: { opacity: 0, x: -80, filter: 'blur(6px)' },
    right: { opacity: 0, x: 80, filter: 'blur(6px)' },
    fade: { opacity: 0, filter: 'blur(8px)' },
    scale: { opacity: 0, scale: 0.9, filter: 'blur(6px)' },
    mask: { opacity: 0, clipPath: 'inset(0 100% 0 0)' },
  };
  const animateTarget = variant === 'mask'
    ? { opacity: 1, clipPath: 'inset(0 0% 0 0)' }
    : { opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)' };

  const Comp = motion[as] || motion.div;

  return (
    <Comp
      initial={initialMap[variant]}
      whileInView={animateTarget}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/** Word-by-word staggered heading */
export const RevealHeading = ({ text, className = '', highlight = null, highlightClassName = '', delay = 0 }) => {
  const words = text.split(' ');
  return (
    <h2 className={className} aria-label={text}>
      {words.map((w, i) => {
        const isHi = highlight && w.toLowerCase().includes(highlight.toLowerCase());
        return (
          <span key={i} className="inline-block overflow-hidden align-baseline">
            <motion.span
              initial={{ y: '110%', opacity: 0 }}
              whileInView={{ y: '0%', opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: delay + i * 0.06, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className={`inline-block ${isHi ? highlightClassName : ''}`}
            >
              {w}{i < words.length - 1 && '\u00a0'}
            </motion.span>
          </span>
        );
      })}
    </h2>
  );
};

/** Parallax translate on scroll */
export const Parallax = ({ children, offset = 80, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

/** Scroll-linked scale-in reveal */
export const ScaleOnScroll = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 90%', 'end 40%'] });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 0.98]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 1], [0.4, 1, 1]);
  return (
    <motion.div ref={ref} style={{ scale, opacity }} className={className}>
      {children}
    </motion.div>
  );
};

export default SectionReveal;
